import { useCallback, useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import { store } from '../../../../store';
import { dopplerApi } from '../dopplerApi';

/**
 * Calculate which tiles are visible in the current map viewport.
 * @param {L.Map} map - Leaflet map instance
 * @returns {{ tiles: Array<{x: number, y: number, z: number}>, zoom: number }}
 */
function getVisibleTiles(map) {
  if (!map) return { tiles: [], zoom: 0 };

  // Check if map is ready (has container and can project)
  try {
    const container = map.getContainer();
    if (!container) return { tiles: [], zoom: 0 };

    const bounds = map.getBounds();
    if (!bounds) return { tiles: [], zoom: 0 };

    const zoom = map.getZoom();
    if (zoom === undefined || zoom === null) return { tiles: [], zoom: 0 };

    const tileSize = 256;

    // Convert lat/lng bounds to tile coordinates
    const nw = map.project(bounds.getNorthWest(), zoom);
    const se = map.project(bounds.getSouthEast(), zoom);

    const minX = Math.floor(nw.x / tileSize);
    const maxX = Math.floor(se.x / tileSize);
    const minY = Math.floor(nw.y / tileSize);
    const maxY = Math.floor(se.y / tileSize);

    const tiles = [];
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        tiles.push({ x, y, z: zoom });
      }
    }

    return { tiles, zoom };
  } catch (e) {
    // Map not ready yet
    return { tiles: [], zoom: 0 };
  }
}

/**
 * Hook to prefetch upcoming animation frames using RTK Query.
 * Prefetches tiles for the next N frames based on current viewport.
 *
 * @param {Object} options
 * @param {Date} options.baseTime - Rounded base time for tile URLs
 * @param {Date[]} options.frames - Array of frame times
 * @param {number} options.currentFrame - Current frame index
 * @param {boolean} options.isPlaying - Whether animation is playing
 * @param {number} options.prefetchCount - Number of frames to prefetch ahead (default: 3)
 * @param {string} options.layer - Layer type (default: 'future_radar')
 * @returns {{ prefetchStatus: Object, prefetchFrame: Function, clearCache: Function }}
 */
export function useDopplerPrefetch({
  baseTime,
  frames,
  currentFrame,
  isPlaying,
  prefetchCount = 3,
  layer = 'future_radar',
}) {
  const map = useMap();
  const prefetchedRef = useRef(new Set());
  const [prefetchStatus, setPrefetchStatus] = useState({});

  // Prefetch a specific frame's tiles
  const prefetchFrame = useCallback(
    async (frameIndex) => {
      if (!map || !frames[frameIndex] || !baseTime) return;

      const frameTime = frames[frameIndex];
      const cacheKey = `${frameIndex}-${frameTime.toISOString()}`;

      // Skip if already prefetched
      if (prefetchedRef.current.has(cacheKey)) return;

      const { tiles, zoom } = getVisibleTiles(map);
      if (tiles.length === 0) return;

      prefetchedRef.current.add(cacheKey);
      setPrefetchStatus((prev) => ({ ...prev, [frameIndex]: 'loading' }));

      try {
        // Dispatch prefetch for each visible tile
        const promises = tiles.map((tile) =>
          store.dispatch(
            dopplerApi.endpoints.getMapTile.initiate({
              baseTime: baseTime.toISOString(),
              frameTime: frameTime.toISOString(),
              layer,
              z: tile.z,
              x: tile.x,
              y: tile.y,
            }),
          ),
        );

        await Promise.all(promises);
        setPrefetchStatus((prev) => ({ ...prev, [frameIndex]: 'loaded' }));
      } catch (error) {
        console.error(`[Doppler] Failed to prefetch frame ${frameIndex}:`, error);
        setPrefetchStatus((prev) => ({ ...prev, [frameIndex]: 'error' }));
        prefetchedRef.current.delete(cacheKey);
      }
    },
    [map, frames, baseTime, layer],
  );

  // Auto-prefetch upcoming frames when playing or frame changes
  useEffect(() => {
    if (!map || !frames.length || !baseTime) return;

    // Prefetch current frame and next N frames
    const framesToPrefetch = [];
    for (let i = 0; i <= prefetchCount; i++) {
      const frameIndex = (currentFrame + i) % frames.length;
      framesToPrefetch.push(frameIndex);
    }

    // Stagger prefetch to avoid overwhelming the server
    framesToPrefetch.forEach((frameIndex, i) => {
      setTimeout(() => prefetchFrame(frameIndex), i * 100);
    });
  }, [currentFrame, map, frames, baseTime, prefetchCount, prefetchFrame]);

  // Prefetch all frames on initial mount (with stagger)
  useEffect(() => {
    if (!map || !frames.length || !baseTime) return;

    // Wait for map to be ready, then prefetch all frames
    const timeoutId = setTimeout(() => {
      frames.forEach((_, frameIndex) => {
        setTimeout(() => prefetchFrame(frameIndex), frameIndex * 200);
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [map, frames.length, baseTime, prefetchFrame]);

  // Clear cache when baseTime changes (new animation cycle)
  const clearCache = useCallback(() => {
    prefetchedRef.current.clear();
    setPrefetchStatus({});
  }, []);

  // Memoize progress calculation to avoid infinite loops
  const getPrefetchProgress = useCallback(() => {
    const total = frames.length;
    const loaded = Object.values(prefetchStatus).filter((s) => s === 'loaded').length;
    return { loaded, total, percent: total > 0 ? Math.round((loaded / total) * 100) : 0 };
  }, [frames.length, prefetchStatus]);

  return {
    prefetchStatus,
    prefetchFrame,
    clearCache,
    getPrefetchProgress,
  };
}

/**
 * Hook variant for use outside of MapContainer (doesn't use useMap).
 * Pass the map ref instead.
 */
export function useDopplerPrefetchWithRef({
  mapRef,
  baseTime,
  frames,
  currentFrame,
  isPlaying,
  prefetchCount = 3,
  layer = 'future_radar',
}) {
  const prefetchedRef = useRef(new Set());
  const [prefetchStatus, setPrefetchStatus] = useState({});

  const prefetchFrame = useCallback(
    async (frameIndex) => {
      const map = mapRef?.current;
      if (!map || !frames[frameIndex] || !baseTime) return;

      const frameTime = frames[frameIndex];
      const cacheKey = `${frameIndex}-${frameTime.toISOString()}`;

      if (prefetchedRef.current.has(cacheKey)) return;

      const { tiles } = getVisibleTiles(map);
      if (tiles.length === 0) return;

      prefetchedRef.current.add(cacheKey);
      setPrefetchStatus((prev) => ({ ...prev, [frameIndex]: 'loading' }));

      try {
        const promises = tiles.map((tile) =>
          store.dispatch(
            dopplerApi.endpoints.getMapTile.initiate({
              baseTime: baseTime.toISOString(),
              frameTime: frameTime.toISOString(),
              layer,
              z: tile.z,
              x: tile.x,
              y: tile.y,
            }),
          ),
        );

        await Promise.all(promises);
        setPrefetchStatus((prev) => ({ ...prev, [frameIndex]: 'loaded' }));
      } catch (error) {
        console.error(`[Doppler] Failed to prefetch frame ${frameIndex}:`, error);
        setPrefetchStatus((prev) => ({ ...prev, [frameIndex]: 'error' }));
        prefetchedRef.current.delete(cacheKey);
      }
    },
    [mapRef, frames, baseTime, layer],
  );

  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !frames.length || !baseTime) return;

    const framesToPrefetch = [];
    for (let i = 0; i <= prefetchCount; i++) {
      const frameIndex = (currentFrame + i) % frames.length;
      framesToPrefetch.push(frameIndex);
    }

    framesToPrefetch.forEach((frameIndex, i) => {
      setTimeout(() => prefetchFrame(frameIndex), i * 100);
    });
  }, [currentFrame, mapRef, frames, baseTime, prefetchCount, prefetchFrame]);

  const clearCache = useCallback(() => {
    prefetchedRef.current.clear();
    setPrefetchStatus({});
  }, []);

  // Memoize progress calculation
  const getPrefetchProgress = useCallback(() => {
    const total = frames.length;
    const loaded = Object.values(prefetchStatus).filter((s) => s === 'loaded').length;
    return { loaded, total, percent: total > 0 ? Math.round((loaded / total) * 100) : 0 };
  }, [frames.length, prefetchStatus]);

  return {
    prefetchStatus,
    prefetchFrame,
    clearCache,
    getPrefetchProgress,
  };
}

export default useDopplerPrefetch;
